/**
 * One insertable variable. `token` is the BARE name (`first_name`) — the
 * variable components (insert menu, typeahead, suggestion list) wrap it in
 * braces themselves, so a pre-wrapped token would render/insert as `{{{{…}}}}`.
 *
 * `label` is what a person reads and is the PRIMARY line in every picker; the
 * raw token is shown dimmed underneath. Producers should always supply a real
 * human label — falling back to the token means the row reads as code.
 */
export interface IVariableItem {
    readonly token: string;
    readonly label: string;
    /** One line explaining what the value is, shown under the label. */
    readonly description?: string;
    /**
     * Value type, when the producer knows it. Drives the type badge and the
     * "this renders empty" warning — `object` / `array` render to nothing in
     * message bodies unless the caller opts into structure serialization.
     */
    readonly type?: VariableValueType;
    /** Example rendered value, e.g. `Sarah Carter`. */
    readonly example?: string;
}

export type VariableValueType = 'string' | 'number' | 'boolean' | 'date' | 'object' | 'array';

/**
 * Icon shown beside a group heading, and the source of that group's tint. A
 * closed set rather than a free-form lucide name so every picker renders the
 * same glyph for the same concept.
 *
 * `extracted` is the author's own `ai.extract` fields, kept distinct from `ai`
 * (the node's fixed outputs) so the two read as the different things they are.
 */
export type VariableIconName =
    | 'contact'
    | 'organization'
    | 'custom'
    | 'trigger'
    | 'step'
    | 'ai'
    | 'extracted'
    | 'variable';

export interface IVariableGroup {
    readonly label: string;
    readonly items: readonly IVariableItem[];
    readonly icon?: VariableIconName;
}

/**
 * Types that render to an empty string in a plain message body. The backend
 * renderer only serializes structures when the caller opts in
 * (`serializeStructures`, which today only the AI nodes do), so inserting one
 * of these into an SMS or email silently produces nothing.
 */
export function rendersEmpty(item: IVariableItem): boolean {
    return item.type === 'object' || item.type === 'array';
}
